import React, { useState } from 'react';
import classNames from 'classnames';
import { merge } from 'lodash';
import { useDispatch } from 'react-redux';

import { Pencil, Plus, Trash2 } from 'lucide-react';
import Select from '../../components/Select';
import Textarea from '../../components/Textarea';
import NumberInput from '../../components/NumberInput';
import { SELECT_STYLES_MODAL } from '../../utils/const';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import Modal from '../../components/Modal';

export default function CardTable({
  editMode = false,
  className,
  titles,
  collection,
  collectionEditSettings,
  title,
  maxCount,
  addNewLabel,
  addHandler,
  updateHandler,
  removeHandler,
  titleStyle,
  textStyle,
  backgroundCard,
  titleColor,
  textColor,
  locales,
}) {
  const optionStyles = {
    option: (styles) => {
      return {
        ...styles,
        fontFamily: 'inherit',
      };
    },
  };

  const [openModals, setOpenModals] = useState({});
  const [openPreviewModals, setOpenPreviewModals] = useState({});

  const [
    isPeriodConfirmationModalVisible,
    setIsPeriodConfirmationModalVisible,
  ] = useState(false);
  const [confirmationModalItemToUpdate, setConfirmationModalItemToUpdate] =
    useState(null);

  const {
    cost: { updateCost },
  } = useDispatch();

  // Helper functions for modal state management
  const openModal = (itemId) => {
    setOpenModals((prev) => ({ ...prev, [itemId]: true }));
  };

  const closeModal = (itemId) => {
    setOpenModals((prev) => ({ ...prev, [itemId]: false }));
  };

  const isModalOpen = (itemId) => {
    return openModals[itemId] || false;
  };

  // Helper functions for preview modal state management
  const openPreviewModal = (itemId) => {
    setOpenPreviewModals((prev) => ({ ...prev, [itemId]: true }));
  };

  const closePreviewModal = (itemId) => {
    setOpenPreviewModals((prev) => ({ ...prev, [itemId]: false }));
  };

  const isPreviewModalOpen = (itemId) => {
    return openPreviewModals[itemId] || false;
  };

  const buildField = (data, { name, type, options }, index) => {
    switch (type) {
      case 'text':
        return (
          <Textarea
            value={data[name]}
            delay={700}
            className={`bmc-form-textarea ${textStyle} custom-font-body `}
            onChange={(e) =>
              updateHandler({ id: data.id, name, value: e.target.value })
            }
          />
        );
      case 'number':
        return (
          <>
            {data?.period === 'Manual' ? (
              <>
                {/* <NumberInput
                  disabled
                  value={`${data[name]?.[0] ?? 0}...`}
                  className={`bmc-form-textarea ${textStyle} custom-font-body`}
                  onChange={() => {}}
                /> */}
                <Button
                  size="evenSm"
                  variant="outline"
                  className="ml-2"
                  onClick={() => openModal(data.id)}
                >
                  <Pencil className="w-6 h-6 text-muted-darker" />
                </Button>
                <Modal
                  className="!w-[1000px]"
                  headerDivider={false}
                  title={locales.costRevenue.fixedCost.tabs.title}
                  isOpen={isModalOpen(data.id)}
                  onClose={() => {
                    closeModal(data.id);
                  }}
                >
                  <div className="w-full overflow-x-auto overflow-y-hidden max-w-none h-full">
                    <table className="table-auto border-separate border-spacing-0 w-max min-w-full">
                      <thead className="w-full">
                        <tr>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                            (headItem, headItemIdx) => (
                              <th
                                key={headItemIdx}
                                className="p-2 min-w-16 text-center text-sm whitespace-nowrap"
                              >
                                {
                                  locales.costRevenue.fixedCost.tabs
                                    .monthAbbreviation
                                }{' '}
                                {headItem}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>
                      <tbody className="w-full">
                        <tr>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                            (bodyItem, bodyItemIdx) => {
                              const isFirst = bodyItemIdx === 0;
                              const isLast = bodyItemIdx === 11;

                              return (
                                <td
                                  key={bodyItemIdx}
                                  className={`p-1 py-1.5 text-center border border-muted-darker bg-muted-lighter ${
                                    isFirst ? 'rounded-l-md' : ''
                                  } ${isLast ? 'rounded-r-md' : ''}`}
                                >
                                  <div className="input-wrap relative">
                                    {/* This span acts as a "measuring stick" - it contains the same text as the input but is invisible. The browser calculates its natural width based on the content. */}
                                    <span
                                      className="width-machine p-2 text-center text-slate-900 invisible whitespace-nowrap"
                                      aria-hidden="true"
                                    >
                                      {data[name]?.[bodyItemIdx] ?? 0}
                                    </span>
                                    <input
                                      type="text"
                                      value={data[name]?.[bodyItemIdx] ?? 0}
                                      onChange={(e) => {
                                        const { value } = e.target;
                                        // Only allow positive numbers (including decimals)
                                        const positiveNumberRegex =
                                          /^[0-9]*\.?[0-9]*$/;
                                        if (
                                          value &&
                                          !positiveNumberRegex.test(value)
                                        ) {
                                          e.target.value =
                                            data[name]?.[bodyItemIdx] ?? 0;
                                          return;
                                        }

                                        // Defining a limit as parseInt can deal with a limited number
                                        const maxSafeNumberStringLength = 12;
                                        if (
                                          value &&
                                          value.length >
                                            maxSafeNumberStringLength
                                        ) {
                                          e.target.value =
                                            data[name]?.[bodyItemIdx] ?? 0;
                                          return;
                                        }

                                        // Parse the value and update if valid
                                        const parsedValue = parseFloat(value);
                                        if (
                                          !Number.isNaN(parsedValue) &&
                                          parsedValue >= 0
                                        ) {
                                          updateCost({
                                            index,
                                            name: 'amount',
                                            valueIndex: bodyItemIdx,
                                            value: parsedValue,
                                            id: data.id,
                                          });
                                        } else if (value === '') {
                                          // Allow empty input
                                          updateCost({
                                            index,
                                            name: 'amount',
                                            valueIndex: bodyItemIdx,
                                            value: 0,
                                            id: data.id,
                                          });
                                        } else {
                                          e.target.value =
                                            data[name]?.[bodyItemIdx] ?? 0;
                                        }
                                      }}
                                      onKeyUp={(e) => {
                                        // Update the width machine with current input value after the key is released
                                        const widthMachine =
                                          e.target.parentElement.querySelector(
                                            '.width-machine',
                                          );
                                        if (widthMachine) {
                                          widthMachine.textContent =
                                            e.target.value || '0';
                                        }
                                      }}
                                      onFocus={(e) => {
                                        // Select all text when focused for easy editing
                                        e.target.select();
                                      }}
                                      className="input absolute w-full h-full p-2 text-center bg-transparent border-0 text-slate-900 focus:outline-none focus:ring-0 top-0 left-0"
                                      placeholder="0"
                                    />
                                  </div>
                                </td>
                              );
                            },
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Modal>
              </>
            ) : (
              <NumberInput
                doFloor={data?.type === 'cost'}
                value={Array.isArray(data[name]) ? data[name][0] : data[name]}
                className={`bmc-form-textarea ${textStyle} custom-font-body`}
                onChange={(e) =>
                  updateHandler({
                    id: data.id,
                    name,
                    value: Number(e.target.value),
                    valueIndex: 0,
                  })
                }
              />
            )}
          </>
        );
      case 'select':
        return (
          <div className="custom-font-body">
            <Select
              className={`bmc-form-select ${textStyle} custom-font-body min-w-40`}
              styles={merge(SELECT_STYLES_MODAL, optionStyles)}
              options={options}
              value={options.find((opt) => opt.value === data[name]) || null}
              onChange={({ value }) => {
                const updatedItem = {
                  name,
                  value,
                  id: data.id,
                };

                const previousValue = data.period;
                if (
                  (previousValue === 'Manual' && value !== 'Manual') ||
                  (previousValue !== 'Manual' && value === 'Manual')
                ) {
                  setConfirmationModalItemToUpdate(updatedItem);
                  setIsPeriodConfirmationModalVisible(true);
                } else {
                  updateHandler({ id: data.id, name, value });
                }
              }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const buildTableDataItem = (item, settings, index) => {
    if (editMode) {
      return buildField(item, settings, index);
    }

    if (settings.type === 'select') {
      const value = item?.[settings.name];
      const matchedOption = settings.options?.find(
        (opt) => opt.value === value,
      );
      return matchedOption?.label ?? value;
    }

    if (settings.name === 'amount') {
      // first check if item[settings.name] is an array or a string
      if (Array.isArray(item[settings.name])) {
        if (item.period === 'Manual') {
          return (
            <>
              {/* <NumberInput
                disabled
                value={`${item[settings.name]?.[0] ?? 0}...`}
                className={`bmc-form-textarea ${textStyle} custom-font-body max-w-20`}
                onChange={() => {}}
              /> */}
              <Button
                size="evenSm"
                variant="outline"
                className="ml-2"
                onClick={() => openPreviewModal(item.id)}
              >
                <Pencil className="w-6 h-6 text-muted-darker" />
              </Button>
              <Modal
                className="!w-[1000px]"
                headerDivider={false}
                title={locales.costRevenue.fixedCost.tabs.title}
                isOpen={isPreviewModalOpen(item.id)}
                onClose={() => {
                  closePreviewModal(item.id);
                }}
              >
                <div className="bg-muted-lighter rounded-lg w-full p-4">
                  <div className="flex w-full overflow-x-auto">
                    <table className="w-full table-auto border-separate border-spacing-0">
                      <thead>
                        <tr>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                            (headItem, headItemIdx) => (
                              <th
                                key={headItemIdx}
                                className="p-2 min-w-16 text-sm"
                              >
                                {
                                  locales.costRevenue.fixedCost.tabs
                                    .monthAbbreviation
                                }{' '}
                                {headItem}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                            (bodyItem, bodyItemIdx) => {
                              const isFirst = bodyItemIdx === 0;
                              const isLast = bodyItemIdx === 11; // 12 items, so last index is 11
                              return (
                                <td
                                  key={bodyItemIdx}
                                  className={`p-2 text-center border border-muted-darker ${
                                    isFirst ? 'rounded-l-md' : ''
                                  } ${isLast ? 'rounded-r-md' : ''}`}
                                >
                                  {item.amount?.[bodyItemIdx] ?? 0}
                                </td>
                              );
                            },
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </Modal>
            </>
          );
        }
        return item[settings.name][0];
      }

      return item[settings.name];
    }

    return item[settings.name];
  };

  return (
    <div
      className={classNames('bmc-page-fincard-content relative', className)}
      style={{ background: backgroundCard }}
    >
      <div
        className={`bmc-page-fincard-title ${titleStyle} custom-font-title`}
        style={{ color: titleColor }}
      >
        {title}
      </div>

      <table
        className={classNames(
          'bmc-page-fincard-table custom-font-body table-fixed',
          textStyle,
          {
            'bmc-page-fincard-table--show': !editMode,
          },
        )}
      >
        <thead>
          <tr>
            {titles.map((item, index) => (
              <th key={index} style={{ color: textColor }}>
                {item}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {collection.map((item) => (
            <tr key={item.id}>
              {collectionEditSettings.map((settings, i) => (
                <td key={i}>
                  {/* <div className="bmc-page-fincard-table-title h-full flex items-center">
                    {titles[i]}
                  </div> */}

                  <div className="flex flex-row items-center h-full flex items-center">
                    {buildTableDataItem(item, settings, i)}
                    {editMode &&
                      i === collectionEditSettings.length - 1 &&
                      collection.length > 1 && (
                        <ConfirmationModal
                          className="ml-3 hover:bg-muted-lighter/60 text-muted-darker"
                          body={locales.common.deleteConfirmation}
                          confirmText="Delete"
                          onConfirm={() => removeHandler({ id: item.id })}
                          buttonAriaLabel="Delete"
                        >
                          <Trash2 className="w-6 min-w-6 h-auto" />
                        </ConfirmationModal>
                      )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {editMode && collection.length < maxCount && (
        <Button variant="outline" onClick={addHandler} className="w-full">
          <Plus className="w-7 h-auto" />
          {addNewLabel}
        </Button>
      )}

      <Modal
        closeButton={false}
        isOpen={isPeriodConfirmationModalVisible}
        onClose={() => setIsPeriodConfirmationModalVisible(false)}
        title={locales.costRevenue.periodChangeConfirmation}
      >
        <div className="flex flex-row gap-3 items-end justify-end w-full">
          <Button onClick={() => setIsPeriodConfirmationModalVisible(false)}>
            {locales.common.cancel}
          </Button>
          <Button
            variant="destructiveOutline"
            onClick={() => {
              updateCost(confirmationModalItemToUpdate);
              updateCost({
                ...confirmationModalItemToUpdate,
                name: 'amount',
                valueIndex: 0,
                value: 0,
                reset: true,
              });
              setIsPeriodConfirmationModalVisible(false);
            }}
          >
            {locales.common.change}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
